import { useCallback, useEffect, useState, type FormEvent } from "react";
import { isAxiosError } from "axios";
import {
  deletePlanRequest,
  uploadPlanRequest,
  getUploadStatusRequest,
  replacePlanTilesRequest,
} from "../../api/adminApi";
import { listPlansRequest } from "../../api/plansApi";
import type { Plan } from "../../types/domain.types";
import { Spinner } from "../UI/Spinner";

function extractErr(e: unknown, fallback: string): string {
  if (isAxiosError(e)) {
    return (
      (e.response?.data as { error?: { message?: string } } | undefined)?.error
        ?.message ?? fallback
    );
  }
  return fallback;
}

export function PlanUploader() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [clientName, setClientName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [processingState, setProcessingState] = useState<"idle" | "uploading" | "processing" | "completed" | "failed">("idle");
  const [totalTiles, setTotalTiles] = useState(0);
  const [uploadedTiles, setUploadedTiles] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Reemplazar tiles de plano existente
  const [replacingPlanId, setReplacingPlanId] = useState<string | null>(null);
  const [replaceProgress, setReplaceProgress] = useState(0);
  const [replaceProcessingState, setReplaceProcessingState] = useState<"idle" | "uploading" | "processing" | "completed" | "failed">("idle");
  const [replaceTotalTiles, setReplaceTotalTiles] = useState(0);
  const [replaceUploadedTiles, setReplaceUploadedTiles] = useState(0);
  const [replaceError, setReplaceError] = useState<string | null>(null);
  const [replaceSuccessMsg, setReplaceSuccessMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setPlans(await listPlansRequest());
    } catch (e) {
      setError(extractErr(e, "Error al cargar planos"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleUpload(e: FormEvent) {
    e.preventDefault();
    if (!file || !name.trim() || !clientName.trim()) return;
    setUploading(true);
    setProcessingState("uploading");
    setProgress(0);
    setTotalTiles(0);
    setUploadedTiles(0);
    setUploadError(null);
    setSuccessMsg(null);
    try {
      const res = await uploadPlanRequest({
        name: name.trim(),
        clientName: clientName.trim(),
        tilesZip: file,
        onProgress: setProgress,
      });

      // Transición a procesamiento en servidor
      setSuccessMsg("Archivo subido con éxito. Iniciando procesamiento en servidor...");
      setProcessingState("processing");

      const pollStatus = async (jobId: string) => {
        try {
          const statusRes = await getUploadStatusRequest(jobId);
          if (statusRes.status === "completed") {
            setSuccessMsg(`Plano "${statusRes.name}" procesado con éxito y activado.`);
            setProcessingState("completed");
            setUploading(false);
            setName("");
            setClientName("");
            setFile(null);
            setFileInputKey((k) => k + 1);
            setProgress(0);
            await load();
          } else if (statusRes.status === "failed") {
            setUploadError(statusRes.error ?? "El procesamiento del plano falló en el servidor.");
            setProcessingState("failed");
            setUploading(false);
          } else {
            // "processing"
            setTotalTiles(statusRes.totalTiles);
            setUploadedTiles(statusRes.uploadedTiles);
            setTimeout(() => void pollStatus(jobId), 2000);
          }
        } catch (pollErr) {
          // Reintentar tras 2 segundos si hay error de red
          setTimeout(() => void pollStatus(jobId), 2000);
        }
      };

      await pollStatus(res.id);
    } catch (err) {
      setUploadError(extractErr(err, "Error al subir el plano"));
      setProcessingState("failed");
      setUploading(false);
    }
  }

  async function handleDelete(plan: Plan) {
    if (
      !window.confirm(
        `¿Eliminar el plano "${plan.name}"? Esta acción borra todos los tiles.`,
      )
    ) {
      return;
    }
    try {
      await deletePlanRequest(plan.id);
      setPlans((list) => list.filter((p) => p.id !== plan.id));
    } catch (e) {
      setError(extractErr(e, "No se pudo eliminar el plano"));
    }
  }

  async function handleReplaceTiles(planId: string, file: File) {
    setReplaceProcessingState("uploading");
    setReplaceProgress(0);
    setReplaceTotalTiles(0);
    setReplaceUploadedTiles(0);
    setReplaceError(null);
    setReplaceSuccessMsg(null);
    try {
      const res = await replacePlanTilesRequest({
        planId,
        tilesZip: file,
        onProgress: setReplaceProgress,
      });

      setReplaceSuccessMsg("Archivo subido. Iniciando procesamiento en servidor...");
      setReplaceProcessingState("processing");

      const pollStatus = async (jobId: string) => {
        try {
          const statusRes = await getUploadStatusRequest(jobId);
          if (statusRes.status === "completed") {
            setReplaceSuccessMsg(`Tiles de "${statusRes.name}" reemplazados con éxito.`);
            setReplaceProcessingState("completed");
            setReplacingPlanId(null);
            setReplaceProgress(0);
            await load();
          } else if (statusRes.status === "failed") {
            setReplaceError(statusRes.error ?? "El reemplazo de tiles falló en el servidor.");
            setReplaceProcessingState("failed");
          } else {
            setReplaceTotalTiles(statusRes.totalTiles);
            setReplaceUploadedTiles(statusRes.uploadedTiles);
            setTimeout(() => void pollStatus(jobId), 2000);
          }
        } catch (pollErr) {
          setTimeout(() => void pollStatus(jobId), 2000);
        }
      };

      await pollStatus(res.id);
    } catch (err) {
      setReplaceError(extractErr(err, "Error al reemplazar tiles"));
      setReplaceProcessingState("failed");
    }
  }

  return (
    <section className="flex flex-col gap-4 rounded-md bg-slate-900/50 p-4 ring-1 ring-slate-800">
      <header>
        <h2 className="text-sm font-semibold text-slate-100">Planos</h2>
        <p className="mt-1 text-xs text-slate-400">
          Sube un ZIP con la estructura{" "}
          <code className="rounded bg-slate-800 px-1">{`{z}/{x}/{y}.png`}</code>{" "}
          generada por <code className="rounded bg-slate-800 px-1">scripts/pdf-to-tiles.py</code>.
          Máx 500 MB.
        </p>
      </header>

      <form
        onSubmit={handleUpload}
        className="grid grid-cols-1 gap-2 rounded-md bg-slate-800/50 p-3 sm:grid-cols-2"
      >
        <input
          type="text"
          required
          maxLength={200}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nombre del plano"
          className="rounded-md bg-slate-800 px-3 py-2 text-sm text-slate-100 ring-1 ring-slate-700 sm:col-span-1"
        />
        <input
          type="text"
          required
          maxLength={200}
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
          placeholder="Cliente"
          className="rounded-md bg-slate-800 px-3 py-2 text-sm text-slate-100 ring-1 ring-slate-700 sm:col-span-1"
        />
        <input
          key={fileInputKey}
          type="file"
          accept=".zip,application/zip,application/x-zip-compressed"
          required
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="text-xs text-slate-300 file:mr-3 file:rounded-md file:border-0 file:bg-brand-light file:px-3 file:py-2 file:text-xs file:font-semibold file:text-white hover:file:bg-brand sm:col-span-2"
        />

        {uploading && (
          <div className="sm:col-span-2 flex flex-col gap-2 rounded-md bg-slate-900/40 p-3 ring-1 ring-slate-800/80">
            {processingState === "uploading" ? (
              <>
                <div className="h-2 w-full overflow-hidden rounded-full bg-slate-800">
                  <div
                    className="h-full bg-brand-light transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-xs text-slate-400">Subiendo archivo: {progress}%…</p>
              </>
            ) : processingState === "processing" ? (
              <>
                <div className="h-2 w-full overflow-hidden rounded-full bg-slate-800">
                  <div
                    className="h-full bg-emerald-500 transition-all"
                    style={{
                      width: `${totalTiles > 0 ? Math.round((uploadedTiles * 100) / totalTiles) : 0}%`,
                    }}
                  />
                </div>
                <p className="text-xs text-emerald-400 flex items-center gap-2">
                  <Spinner size={10} />
                  Procesando en servidor: {uploadedTiles} de {totalTiles} tiles ({totalTiles > 0 ? Math.round((uploadedTiles * 100) / totalTiles) : 0}%)…
                </p>
              </>
            ) : null}
          </div>
        )}

        {uploadError && (
          <p className="sm:col-span-2 text-xs text-red-400" role="alert">
            {uploadError}
          </p>
        )}
        {successMsg && !uploadError && (
          <p className="sm:col-span-2 text-xs text-emerald-400">{successMsg}</p>
        )}

        <button
          type="submit"
          disabled={uploading || !file}
          className="sm:col-span-2 flex items-center justify-center gap-2 rounded-md bg-brand-light px-3 py-2 text-xs font-semibold text-white hover:bg-brand disabled:opacity-50"
        >
          {uploading && <Spinner size={12} />}
          Subir plano
        </button>
      </form>

      {error && (
        <p role="alert" className="text-xs text-red-400">
          {error}
        </p>
      )}

      {loading ? (
        <div className="flex justify-center py-6">
          <Spinner size={24} />
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {plans.map((p) => (
            <li
              key={p.id}
              className="flex flex-col gap-2 rounded-md border border-slate-800 bg-slate-900 p-3 text-sm text-slate-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">{p.name}</div>
                  <div className="text-xs text-slate-400">
                    {p.clientName} · zoom {p.tileMinZoom}–{p.tileMaxZoom} ·{" "}
                    {p.tileWidth}×{p.tileHeight}px
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {replacingPlanId === p.id && replaceProcessingState === "idle" ? (
                    <input
                      type="file"
                      accept=".zip,application/zip,application/x-zip-compressed"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void handleReplaceTiles(p.id, f);
                      }}
                      className="text-xs text-slate-300 file:mr-2 file:rounded file:border-0 file:bg-slate-700 file:px-2 file:py-1 file:text-xs file:text-white"
                    />
                  ) : replacingPlanId === p.id ? (
                    <button
                      onClick={() => {
                        setReplacingPlanId(null);
                        setReplaceProcessingState("idle");
                        setReplaceError(null);
                        setReplaceSuccessMsg(null);
                      }}
                      className="rounded border border-slate-600 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
                    >
                      Cancelar
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        setReplacingPlanId(p.id);
                        setReplaceProcessingState("idle");
                        setReplaceError(null);
                        setReplaceSuccessMsg(null);
                      }}
                      className="rounded border border-sky-500/50 px-3 py-1 text-xs text-sky-300 hover:bg-sky-500/10"
                    >
                      Reemplazar tiles
                    </button>
                  )}
                  <button
                    onClick={() => void handleDelete(p)}
                    className="rounded border border-red-500/50 px-3 py-1 text-xs text-red-300 hover:bg-red-500/10"
                  >
                    Eliminar
                  </button>
                </div>
              </div>

              {replacingPlanId === p.id && replaceProcessingState !== "idle" && (
                <div className="flex flex-col gap-2 rounded-md bg-slate-900/40 p-2 ring-1 ring-slate-800/80">
                  {replaceProcessingState === "uploading" ? (
                    <>
                      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-800">
                        <div
                          className="h-full bg-brand-light transition-all"
                          style={{ width: `${replaceProgress}%` }}
                        />
                      </div>
                      <p className="text-xs text-slate-400">Subiendo archivo: {replaceProgress}%…</p>
                    </>
                  ) : replaceProcessingState === "processing" ? (
                    <>
                      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-800">
                        <div
                          className="h-full bg-emerald-500 transition-all"
                          style={{
                            width: `${replaceTotalTiles > 0 ? Math.round((replaceUploadedTiles * 100) / replaceTotalTiles) : 0}%`,
                          }}
                        />
                      </div>
                      <p className="text-xs text-emerald-400 flex items-center gap-2">
                        <Spinner size={10} />
                        Procesando en servidor: {replaceUploadedTiles} de {replaceTotalTiles} tiles ({replaceTotalTiles > 0 ? Math.round((replaceUploadedTiles * 100) / replaceTotalTiles) : 0}%)…
                      </p>
                    </>
                  ) : null}
                  {replaceError && (
                    <p className="text-xs text-red-400" role="alert">
                      {replaceError}
                    </p>
                  )}
                  {replaceSuccessMsg && !replaceError && (
                    <p className="text-xs text-emerald-400">{replaceSuccessMsg}</p>
                  )}
                </div>
              )}
            </li>
          ))}
          {plans.length === 0 && (
            <li className="text-xs text-slate-500">Sin planos.</li>
          )}
        </ul>
      )}
    </section>
  );
}
